import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, Mail, Calendar, Sparkles, Lock, HelpCircle, Check } from "lucide-react";
import Header from "@/components/Header";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useCalc } from "@/context/CalcContext";
import { BOOKING_URL } from "@/constants/booking";
import useSEO from "@/hooks/useSEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { id: 0, key: "secteur", title: "Votre secteur d'activité", subtitle: "Pour pondérer cyber, audit et turnover par défaut." },
  { id: 1, key: "population", title: "Votre population totale", subtitle: "Internes, prestataires et saisonniers — chaque catégorie compte." },
  { id: 2, key: "flux", title: "Vos flux RH", subtitle: "Turnover et mobilité — le carburant du JML." },
  { id: 3, key: "apps", title: "Vos applications", subtitle: "Le périmètre fonctionnel à gouverner." },
  { id: 4, key: "maturity", title: "Comptes & maturité IAM", subtitle: "Point de départ, gouvernance, privilèges." },
  { id: 5, key: "advanced", title: "Avancé (optionnel)", subtitle: "Personnalisez le coût horaire et les incidents passés." },
  { id: 6, key: "lead", title: "Vos coordonnées", subtitle: "Pour recevoir votre étude PDF brandée Ariovis." },
];

export default function Calculator() {
  const nav = useNavigate();
  const { inputs, setInputs, setResults, lead, setLead } = useCalc();
  const [step, setStep] = useState(0);
  const [sectors, setSectors] = useState([]);
  const [sectorDetails, setSectorDetails] = useState({});
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  useSEO({
    title: "Démarrer mon étude ROI IAM — Questionnaire en 7 étapes | Ariovis",
    description: "Renseignez secteur, population (internes, prestataires, saisonniers), turnover, applications et maturité IAM. Ariovis calcule 3 scénarios documentés.",
    canonical: "https://iam-calculator.emergent.host/calculer",
  });

  useEffect(() => {
    axios.get(`${API}/sectors`).then((r) => {
      setSectors(r.data.sectors);
      const map = {};
      (r.data.details || []).forEach((d) => { map[d.name] = d; });
      setSectorDetails(map);
    }).catch(() => {
      setSectors([
        "Banque / Finance / Assurance", "Santé / Hôpital / Pharma", "Industrie / Manufacturing",
        "Énergie / Utilities", "Secteur Public", "Défense / Aérospatial",
        "Retail / Distribution", "Services / Conseil", "Tech / Software / Telecom",
        "Transport / Logistique", "Hôtellerie / Restauration / Tourisme", "Immobilier", "Autre",
      ]);
    });
  }, []);

  // Reprise de simulation depuis un lien signé (?reprise=<token>)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("reprise");
    if (!token) return;
    axios.get(`${API}/resume/${encodeURIComponent(token)}`)
      .then((r) => {
        if (r.data?.inputs) setInputs((p) => ({ ...p, ...r.data.inputs }));
        if (r.data?.lead) setLead((p) => ({ ...p, ...r.data.lead }));
        toast.success("Simulation reprise — vos données sont pré-remplies.");
        // Nettoie l'URL pour éviter de re-déclencher la reprise sur navigation
        window.history.replaceState({}, "", "/calculer");
      })
      .catch(() => {
        toast.error("Lien de reprise invalide ou expiré.");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = (k, v) => setInputs((p) => ({ ...p, [k]: v }));
  const updateLead = (k, v) => setLead((p) => ({ ...p, [k]: v }));

  // Quand le secteur change : pré-remplir le turnover ET le coût horaire avec les baselines secteur
  const onSectorChange = (newSector) => {
    const detail = sectorDetails[newSector];
    setInputs((p) => ({
      ...p,
      sector: newSector,
      turnover_pct: detail ? detail.turnover_default : p.turnover_pct,
      hourly_user_custom: detail ? detail.hourly_user_default : p.hourly_user_custom,
    }));
  };

  const currentSectorBaseline = sectorDetails[inputs.sector]?.turnover_default;

  const validateLead = () => {
    if (!lead.firstname || !lead.lastname || !lead.email || !lead.company) {
      toast.error("Tous les champs marqués * sont obligatoires.");
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(lead.email)) {
      toast.error("Format d'email invalide.");
      return false;
    }
    return true;
  };

  const onSubmitFinal = async () => {
    if (!validateLead()) return;
    setLoading(true);
    try {
      const finalLead = { ...lead, sector: lead.sector || inputs.sector };
      const { data } = await axios.post(`${API}/submit`, { lead: finalLead, inputs });
      setResults(data.results);
      setLead(finalLead);
      toast.success("Étude envoyée — vous y êtes !");
      nav("/resultats");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Échec de l'envoi.";
      toast.error(`Erreur : ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  const onNext = () => {
    if (step === STEPS.length - 1) {
      onSubmitFinal();
      return;
    }
    setStep(step + 1);
  };

  const onBack = () => (step === 0 ? nav("/") : setStep(step - 1));

  return (
    <div className="min-h-screen bg-slate-50">
      <Header minimal />
      <div className="relative max-w-4xl mx-auto px-6 lg:px-12 pt-10 pb-24">
        <div className="ar-blob bg-fuchsia-500" style={{ width: 320, height: 320, top: -40, right: -100, opacity: 0.18 }} />
        <div className="ar-blob bg-orange-400" style={{ width: 300, height: 300, bottom: 120, left: -120, opacity: 0.15 }} />

        {/* Progress bar */}
        <div className="relative mb-12">
          <div className="flex items-center justify-between mb-4">
            {STEPS.map((s, i) => (
              <div key={s.id} className="flex items-center gap-2 flex-1 last:flex-none">
                <div
                  data-testid={`step-pill-${i}`}
                  className={`w-8 h-8 shrink-0 rounded-full flex items-center justify-center text-xs font-semibold transition-all ${
                    i <= step ? "ar-step-active" : "bg-slate-200 text-slate-500"
                  }`}
                >
                  {i + 1}
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`h-px flex-1 ${i < step ? "bg-slate-900" : "bg-slate-200"}`} />
                )}
              </div>
            ))}
          </div>
          <div className="text-[11px] tracking-[0.18em] uppercase text-slate-500 font-semibold mt-6 mb-2">
            Étape {step + 1} / {STEPS.length}
          </div>
          <h1 className="text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">{STEPS[step].title}</h1>
          <p className="text-sm text-slate-500 mt-2">{STEPS[step].subtitle}</p>
        </div>

        <div className="relative bg-white border border-slate-200 rounded-3xl p-8 lg:p-10">
          {/* STEP 0 - Secteur */}
          {step === 0 && (
            <div className="space-y-6">
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-3 block">Secteur d'activité principal</Label>
                <Select value={inputs.sector} onValueChange={onSectorChange}>
                  <SelectTrigger data-testid="input-sector" className="h-12 text-base">
                    <SelectValue placeholder="Choisissez un secteur" />
                  </SelectTrigger>
                  <SelectContent>
                    {sectors.map((s) => (
                      <SelectItem key={s} value={s} data-testid={`sector-option-${s}`}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-2">
                  Banque / Santé / Énergie → multiplicateur cyber et compliance plus élevé.
                  Hôtellerie / Retail → flux saisonniers importants (à préciser à l'étape suivante).
                </p>
              </div>
            </div>
          )}

          {/* STEP 1 - Population */}
          {step === 1 && (
            <div className="space-y-8">
              <NumberField
                label="Utilisateurs internes (salariés permanents)"
                value={inputs.nb_users}
                onChange={(v) => update("nb_users", v)}
                min={1} max={500000} step={50}
                testid="input-nb-users"
                helper="Les permanents — base de calcul des coûts horaires et du turnover."
                tooltip="Incluez les CDI / fonctionnaires / titulaires présents. Ne comptez pas les prestataires ni les saisonniers (questions séparées en-dessous)."
              />
              <NumberField
                label="Prestataires / consultants externes"
                value={inputs.nb_contractors}
                onChange={(v) => update("nb_contractors", v)}
                min={0} max={100000} step={10}
                testid="input-nb-contractors"
                helper="Identités externes longue durée (ESN, freelances, sous-traitants)."
                tooltip="Externes avec un compte dans votre SI (AD, Entra, M365) — donc générant un coût de gouvernance. Si vous donnez juste un badge physique sans accès numérique, ne comptez pas."
              />
              <NumberField
                label="Saisonniers / intérimaires (effectif simultané)"
                value={inputs.nb_seasonal}
                onChange={(v) => update("nb_seasonal", v)}
                min={0} max={200000} step={10}
                testid="input-nb-seasonal"
                helper="Hôtellerie, restauration, retail, agro-alimentaire : population à très forte rotation."
                tooltip="Nombre de saisonniers présents EN MÊME TEMPS (pas en cumulé sur l'année). Ex : un hôtel avec 200 saisonniers en été + 0 en hiver → mettez 200. Le modèle calcule automatiquement les cycles de rotation."
              />
              {inputs.nb_seasonal > 0 && (
                <SliderField
                  label="Durée moyenne d'une mission saisonnière"
                  value={inputs.seasonal_duration_months}
                  onChange={(v) => update("seasonal_duration_months", v)}
                  min={1} max={12} step={0.5}
                  format={(v) => `${v.toFixed(1)} mois`}
                  helper={`Pour ${inputs.nb_seasonal} saisonniers ↔ ~${Math.round(inputs.nb_seasonal * (12 / Math.max(inputs.seasonal_duration_months, 1)))} JML par an.`}
                  testid="slider-seasonal-duration"
                />
              )}
              <div className="rounded-2xl bg-slate-50 border border-slate-200 p-5 text-sm">
                <div className="flex justify-between items-baseline">
                  <span className="text-slate-600">Population totale calculée</span>
                  <span className="text-2xl font-bold ar-gradient-text" data-testid="pop-total">
                    {(inputs.nb_users + inputs.nb_contractors + inputs.nb_seasonal).toLocaleString("fr-FR")}
                  </span>
                </div>
                <div className="text-[11px] text-slate-500 mt-2">
                  Effectif présent simultanément. Les saisonniers seront comptés en plusieurs cycles par an pour le JML.
                </div>
              </div>

              {inputs.nb_users < 250 && (
                <div
                  data-testid="pme-block"
                  className="relative rounded-2xl border-2 border-fuchsia-200 bg-gradient-to-br from-fuchsia-50 via-white to-orange-50 p-6 lg:p-7 overflow-hidden"
                >
                  <div className="absolute top-0 left-0 right-0 h-1 ar-gradient-bg" />
                  <div className="text-[11px] tracking-[0.18em] uppercase text-fuchsia-700 font-semibold mb-3">
                    Offre dédiée PME
                  </div>
                  <h3 className="text-xl lg:text-2xl font-bold text-slate-900 leading-snug mb-3">
                    PME en recherche de performance&nbsp;?
                  </h3>
                  <p className="text-sm text-slate-700 leading-relaxed mb-5">
                    Contactez-nous pour découvrir nos offres dédiées à votre contexte&nbsp;! Notre calculateur ROI
                    est calibré pour les organisations de <b>250 collaborateurs et plus</b>. En dessous de ce seuil,
                    Ariovis propose une approche sur-mesure&nbsp;: cadrage express, déploiement allégé, tarification
                    adaptée aux PME.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <a
                      href="mailto:contact-projets@ariovis.fr?subject=Demande%20offre%20PME%20-%20IAM%20ROI%20Calculator"
                      data-testid="pme-contact-mail"
                      className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-slate-900 text-white text-sm font-medium hover:bg-slate-950 transition"
                    >
                      <Mail className="w-4 h-4" />
                      contact-projets@ariovis.fr
                    </a>
                    <a
                      href={BOOKING_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="pme-contact-meeting"
                      className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-slate-300 bg-white text-slate-900 text-sm font-medium hover:border-slate-900 transition"
                    >
                      <Calendar className="w-4 h-4" />
                      Réserver un échange
                    </a>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* STEP 2 - Flux RH */}
          {step === 2 && (
            <div className="space-y-10">
              {currentSectorBaseline !== undefined && (
                <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4 text-sm text-slate-700 flex items-start gap-3">
                  <Sparkles className="w-4 h-4 text-fuchsia-600 mt-0.5 shrink-0" />
                  <div>
                    Le turnover ci-dessous a été <b>pré-rempli avec la moyenne sectorielle Ariovis</b> pour
                    « {inputs.sector} » : <b>{(currentSectorBaseline * 100).toFixed(0)} %</b>.
                    Ajustez-le si vous connaissez votre chiffre réel.
                  </div>
                </div>
              )}
              <SliderField
                label="Turnover annuel des internes"
                value={inputs.turnover_pct}
                onChange={(v) => update("turnover_pct", v)}
                min={0.02} max={0.80} step={0.01}
                format={(v) => `${(v * 100).toFixed(0)} %`}
                helper="Part des permanents qui quittent ou rejoignent l'entreprise chaque année."
                tooltip="Le turnover est la rotation du personnel. Pour 1 000 salariés à 10 %, c'est 100 départs et 100 arrivées par an — chaque transition exige une action JML (Joiner-Mover-Leaver) sur les accès. Très élevé dans l'hôtellerie/retail, très bas dans la défense/secteur public."
                testid="slider-turnover"
              />
              <SliderField
                label="Rotation des prestataires (cycles annuels moyens)"
                value={inputs.turnover_contractor_pct}
                onChange={(v) => update("turnover_contractor_pct", v)}
                min={0.10} max={2.0} step={0.05}
                format={(v) => `${(v * 100).toFixed(0)} %`}
                helper="Les missions tournent plus vite qu'un CDI. À 100 %, on suppose un renouvellement complet par an."
                tooltip="Les prestataires (ESN, freelances) sont sur des missions plus courtes. 50 % = la moitié change chaque année, 200 % = renouvellement deux fois par an. Si vous ne savez pas, 40-60 % est une bonne moyenne."
                testid="slider-contractor-turnover"
              />
              <SliderField
                label="Mobilité interne annuelle (Movers)"
                value={inputs.mover_pct}
                onChange={(v) => update("mover_pct", v)}
                min={0.02} max={0.40} step={0.01}
                format={(v) => `${(v * 100).toFixed(0)} %`}
                helper="Changements de poste, promotions, mutations — déclencheurs de modifications d'accès."
                tooltip="Quand un salarié change de poste, ses anciens accès doivent être retirés et de nouveaux ouverts. Ce mouvement (le M de JML) est aussi coûteux qu'une arrivée et souvent oublié. Typique : 10-20 % par an."
                testid="slider-mover"
              />
            </div>
          )}

          {/* STEP 3 - Applications */}
          {step === 3 && (
            <div className="space-y-8">
              <NumberField
                label="Nombre total d'applications dans le périmètre IAM"
                value={inputs.nb_apps}
                onChange={(v) => update("nb_apps", v)}
                min={1} max={2000} step={5}
                helper="SaaS + on-premise. M365, Salesforce, ERPs, apps métiers..."
                tooltip="Le périmètre IAM = toute application qui crée un compte utilisateur identifiable. Pour une PME, 30-80 apps est courant. Pour un grand groupe, 200-500. Incluez aussi les apps SaaS adoptées sans validation DSI (« shadow IT »)."
                testid="input-nb-apps"
              />
              <NumberField
                label="Applications critiques (revues d'accès obligatoires)"
                value={inputs.nb_apps_critical ?? Math.min(40, inputs.nb_apps)}
                onChange={(v) => update("nb_apps_critical", v)}
                min={0} max={500} step={1}
                helper="SOX, NIS2, DORA, GDPR, PCI, données sensibles."
                tooltip="Applications soumises à un cadre réglementaire ou contenant des données sensibles (financières, RH, santé, secrets industriels). Pour ces apps, une revue d'accès au moins annuelle est requise."
                testid="input-nb-apps-critical"
              />
              <SliderField
                label="Part d'applications en SaaS"
                value={inputs.nb_apps_saas_pct}
                onChange={(v) => update("nb_apps_saas_pct", v)}
                min={0.1} max={1.0} step={0.05}
                format={(v) => `${(v * 100).toFixed(0)} %`}
                helper="Plus le ratio SaaS est élevé, plus le SAM (gestion licences) génère de gains."
                testid="slider-saas-pct"
              />
            </div>
          )}

          {/* STEP 4 - Maturity & Governance */}
          {step === 4 && (
            <div className="space-y-8">
              <NumberField
                label="Comptes à privilèges (administrateurs, comptes de service)"
                value={inputs.nb_priv ?? Math.round(inputs.nb_users * 0.07)}
                onChange={(v) => update("nb_priv", v)}
                min={0} max={50000} step={5}
                helper="Par défaut, 7 % des utilisateurs internes (benchmark Gartner)."
                tooltip="Comptes ayant des droits étendus : administrateurs Windows/Linux, DBA, comptes de service, comptes d'application. Les fuites sur ces comptes ont un impact 5 à 10× plus élevé qu'un compte standard — ils justifient une protection PAM dédiée."
                testid="input-nb-priv"
              />
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-3 block">Maturité IAM actuelle</Label>
                <RadioGroup value={inputs.maturity} onValueChange={(v) => update("maturity", v)} className="grid md:grid-cols-3 gap-3">
                  {[
                    { v: "manuel", t: "Manuel / fragmenté", d: "Tickets, scripts, peu d'automatisation." },
                    { v: "partiel", t: "Partiellement industrialisé", d: "Provisioning automatisé, gouvernance partielle." },
                    { v: "mature", t: "Mature", d: "IGA + PAM + MFA + revues automatisées." },
                  ].map((m) => (
                    <label
                      key={m.v}
                      data-testid={`radio-maturity-${m.v}`}
                      className={`relative cursor-pointer rounded-2xl border p-5 transition-all ${
                        inputs.maturity === m.v
                          ? "border-slate-900 bg-slate-50 shadow-sm"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <RadioGroupItem value={m.v} className="absolute top-4 right-4" />
                      <div className="text-sm font-semibold text-slate-900">{m.t}</div>
                      <div className="text-xs text-slate-500 mt-1.5 leading-relaxed">{m.d}</div>
                    </label>
                  ))}
                </RadioGroup>

                <details className="mt-4 group rounded-2xl bg-slate-50 border border-slate-200" data-testid="maturity-help">
                  <summary className="cursor-pointer list-none px-5 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-slate-900 flex items-center justify-center">
                        <Sparkles className="w-3 h-3 text-white" />
                      </span>
                      <span className="text-sm font-semibold text-slate-900">Comment évaluer ma maturité IAM en 2 minutes ?</span>
                    </div>
                    <span className="text-slate-500 group-open:rotate-45 transition-transform text-lg">+</span>
                  </summary>
                  <div className="px-5 pb-5 grid md:grid-cols-3 gap-3 text-xs">
                    {[
                      {
                        level: "Manuel / fragmenté",
                        color: "border-l-orange-500",
                        signs: [
                          "Les arrivées passent par un ticket + des scripts PowerShell.",
                          "Pas (ou peu) de désactivation automatique au départ.",
                          "Les droits sont saisis à la main dans chaque application.",
                          "Les revues d'accès sont exceptionnelles, sur Excel.",
                          "Pas de coffre PAM, ou outil utilisé partiellement.",
                        ],
                      },
                      {
                        level: "Partiellement industrialisé",
                        color: "border-l-fuchsia-500",
                        signs: [
                          "Création de comptes automatisée depuis le SIRH pour ≥ 1 application clé (AD, Entra ID).",
                          "Catalogue de demandes d'accès via ITSM (ServiceNow…).",
                          "Quelques revues annuelles partiellement automatisées.",
                          "PAM déployé pour les administrateurs DSI seulement.",
                          "MFA pour les apps critiques mais inconsistant.",
                        ],
                      },
                      {
                        level: "Mature",
                        color: "border-l-slate-900",
                        signs: [
                          "IGA en place (SailPoint, Saviynt, Netwrix, Omada…) avec workflows JML automatisés.",
                          "Modèle de rôles maintenu, recertification semestrielle systématique.",
                          "PAM industrialisé (CyberArk, Delinea…), rotation auto des secrets.",
                          "MFA / passwordless sur l'ensemble des accès sensibles.",
                          "Réconciliation des comptes orphelins mensuelle.",
                        ],
                      },
                    ].map((m) => (
                      <div key={m.level} className={`bg-white rounded-xl p-4 border border-slate-200 border-l-4 ${m.color}`}>
                        <div className="text-[11px] tracking-[0.18em] uppercase text-slate-500 font-semibold mb-2">{m.level}</div>
                        <ul className="space-y-1.5">
                          {m.signs.map((s, i) => (
                            <li key={i} className="text-slate-700 leading-snug flex gap-2">
                              <span className="text-slate-400 mt-0.5 shrink-0">→</span>
                              <span>{s}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                    <div className="md:col-span-3 mt-2 text-[11px] text-slate-500 italic leading-relaxed">
                      Auto-évaluation : comptez le nombre de « signes » qui vous correspondent dans chaque colonne. La majorité d'entre eux décide votre niveau. En cas de doute entre deux niveaux, choisissez le plus prudent — votre étude n'en sera que plus crédible.
                    </div>
                  </div>
                </details>
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-3 block">Gestion de l'écosystème IAM</Label>
                <RadioGroup value={inputs.governance} onValueChange={(v) => update("governance", v)} className="grid md:grid-cols-3 gap-3">
                  {[
                    { v: "centralisee", t: "Centralisée", d: "Une équipe IAM unique, processus standardisés." },
                    { v: "hybride", t: "Hybride", d: "Équipe IAM + relais métiers / BU." },
                    { v: "decentralisee", t: "Décentralisée", d: "Chaque BU gère ses accès — gisement de gains SAM important." },
                  ].map((g) => (
                    <label
                      key={g.v}
                      data-testid={`radio-governance-${g.v}`}
                      className={`relative cursor-pointer rounded-2xl border p-5 transition-all ${
                        inputs.governance === g.v
                          ? "border-fuchsia-600 bg-fuchsia-50/40"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <RadioGroupItem value={g.v} className="absolute top-4 right-4" />
                      <div className="text-sm font-semibold text-slate-900">{g.t}</div>
                      <div className="text-xs text-slate-500 mt-1.5 leading-relaxed">{g.d}</div>
                    </label>
                  ))}
                </RadioGroup>
              </div>
            </div>
          )}

          {/* STEP 5 - Avancé */}
          {step === 5 && (
            <div className="space-y-8">
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-3 block">
                  Avez-vous déjà un outil IAM en place ?
                </Label>
                <RadioGroup
                  value={inputs.existing_tool_status}
                  onValueChange={(v) => {
                    update("existing_tool_status", v);
                    update("has_existing_iam_tool", v !== "scripts");
                  }}
                  className="grid md:grid-cols-3 gap-3"
                >
                  {[
                    { v: "scripts", t: "Non — scripts / processus manuels", d: "PowerShell, Excel, tickets ServiceNow ad-hoc." },
                    { v: "partial_tool", t: "Partiellement — outil legacy", d: "IGA ancien, sous-utilisé, à remplacer ou consolider." },
                    { v: "full_tool", t: "Oui — un outil IAM industriel", d: "SailPoint, Saviynt, Okta, OneIdentity… à remplacer." },
                  ].map((m) => {
                    const selected = inputs.existing_tool_status === m.v;
                    return (
                      <label
                        key={m.v}
                        data-testid={`radio-existingtool-${m.v}`}
                        className={`relative cursor-pointer rounded-2xl border p-5 pr-12 transition-all ${
                          selected
                            ? "border-slate-900 bg-slate-50 ring-1 ring-slate-900"
                            : "border-slate-200 hover:border-slate-300"
                        }`}
                      >
                        <RadioGroupItem value={m.v} className="sr-only" />
                        <div className={`absolute top-4 right-4 w-5 h-5 rounded-full flex items-center justify-center transition-all ${
                          selected ? "bg-slate-900" : "border border-slate-300 bg-white"
                        }`}>
                          {selected && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                        </div>
                        <div className="text-sm font-semibold text-slate-900 leading-snug">{m.t}</div>
                        <div className="text-xs text-slate-500 mt-1.5 leading-relaxed">{m.d}</div>
                      </label>
                    );
                  })}
                </RadioGroup>
                {inputs.has_existing_iam_tool && (
                  <div className="mt-4 rounded-xl bg-orange-50 border border-orange-200 p-4 text-sm text-slate-700">
                    <div className="font-semibold text-orange-900 mb-1">Branche migration activée</div>
                    Un projet de remplacement nécessite typiquement <b>6 à 12 mois supplémentaires</b> (selon
                    la taille) pour décommissionner l'existant, faire du parallel run et reprendre les règles.
                    Ticket d'investissement minimal : <b>50 k€</b>. Le ROI ne démarre qu'après cette
                    bascule.
                  </div>
                )}
              </div>

              <div>
                <Label className="text-sm font-medium text-slate-700 mb-3 block">
                  Incidents liés aux identités sur les 3 dernières années
                </Label>
                <div className="grid grid-cols-5 gap-3">
                  {[0, 1, 2, 3, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      data-testid={`incidents-${n}`}
                      onClick={() => update("incidents_last_3y", n)}
                      className={`h-16 rounded-xl border text-lg font-semibold transition-all ${
                        inputs.incidents_last_3y === n
                          ? "border-fuchsia-600 bg-fuchsia-50/40 text-fuchsia-700"
                          : "border-slate-200 text-slate-700 hover:border-slate-300"
                      }`}
                    >
                      {n === 5 ? "5+" : n}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-slate-500 mt-3">
                  Compromission d'utilisateur, abus de compte orphelin, fuite via excès de droits, ransomware via identité, etc.
                </p>
              </div>

              <details className="rounded-2xl border border-slate-200 p-6 group" data-testid="advanced-compliance">
                <summary className="cursor-pointer list-none flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-medium text-slate-700 cursor-pointer">Paramètres avancés (compliance, accès Day-1)</Label>
                    <p className="text-xs text-slate-500 mt-1">Optionnel — affinez votre étude.</p>
                  </div>
                  <span className="text-slate-400 group-open:rotate-45 transition-transform text-xl">+</span>
                </summary>
                <div className="mt-6 space-y-6">
                  <SliderField
                    label="Délai actuel d'accès Day-1 (nouvel arrivant)"
                    value={inputs.delay_access_d1_days}
                    onChange={(v) => update("delay_access_d1_days", v)}
                    min={0.04} max={10} step={0.5}
                    format={(v) => v < 1 ? `${Math.round(v * 24)}h` : `${v.toFixed(1)} jours`}
                    helper="Temps moyen entre arrivée et accès complet. Objectif IAM : 1h."
                    testid="slider-delay-d1"
                  />
                  <NumberField
                    label="Délai moyen de désactivation après un départ (jours)"
                    value={inputs.days_to_revoke_leaver}
                    onChange={(v) => update("days_to_revoke_leaver", v)}
                    min={0} max={180} step={1}
                    helper="Délai entre dernier jour effectif et retrait complet des accès."
                    testid="input-revoke-days"
                  />
                  <NumberField
                    label="Campagnes de revue d'accès / an"
                    value={inputs.nb_review_campaigns_yr}
                    onChange={(v) => update("nb_review_campaigns_yr", v)}
                    min={0} max={12} step={1}
                    helper="Typiquement 1-2 pour des apps critiques (SOX, NIS2, DORA)."
                    testid="input-review-campaigns"
                  />
                  <NumberField
                    label="Heures annuelles consacrées aux audits / preuves"
                    value={inputs.audit_hours_yr}
                    onChange={(v) => update("audit_hours_yr", v)}
                    min={0} max={5000} step={20}
                    helper="Préparation, collecte d'evidences, réponses aux auditeurs."
                    testid="input-audit-hours"
                  />
                </div>
              </details>

              <div className="rounded-2xl border border-slate-200 p-6">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Coût horaire interne personnalisé</Label>
                    <p className="text-xs text-slate-500 mt-1">
                      Pré-rempli depuis votre secteur (<b className="text-slate-900">{sectorDetails[inputs.sector]?.hourly_user_default ?? 55} €/h</b> pour {inputs.sector}). Activez pour affiner.
                    </p>
                  </div>
                  <Switch
                    checked={inputs.use_custom_hourly}
                    onCheckedChange={(v) => update("use_custom_hourly", v)}
                    data-testid="switch-custom-hourly"
                  />
                </div>
                {inputs.use_custom_hourly && (
                  <SliderField
                    label="Coût horaire moyen utilisateur (chargé)"
                    value={inputs.hourly_user_custom ?? sectorDetails[inputs.sector]?.hourly_user_default ?? 55}
                    onChange={(v) => update("hourly_user_custom", v)}
                    min={20} max={250} step={5}
                    format={(v) => `${v} €/h`}
                    helper="Banque/Finance ~80 €, Tech ~75 €, Conseil ~65 €, Énergie ~60 €, Santé ~50 €, Industrie ~45 €, Public ~40 €, Transport ~38 €, Retail ~30 €, Hôtellerie ~28 €."
                    testid="slider-hourly-user"
                  />
                )}
              </div>

              <div className="rounded-2xl bg-slate-50 border border-slate-200 p-5 text-xs text-slate-500 leading-relaxed">
                <span className="font-semibold text-slate-700">À noter :</span> ce calculateur quantifie le ROI agrégé
                (gains opérationnels, SAM, risque). La <b>cohérence des règles d'accès</b> (modèle de rôles, séparation
                des tâches, qualité d'attestation) fait l'objet d'un autre calculateur Ariovis dédié. Pour une
                évaluation conjointe, contactez l'équipe.
              </div>
            </div>
          )}

          {/* STEP 6 - Lead form (GATE) */}
          {step === 6 && (
            <div className="space-y-6">
              {/* Teaser flouté */}
              {preview && (
                <div className="relative rounded-2xl border border-slate-200 overflow-hidden bg-white">
                  <div className="grid grid-cols-3 divide-x divide-slate-200 filter blur-md select-none pointer-events-none" data-testid="teaser-blurred">
                    <div className="p-5 text-center">
                      <div className="text-[10px] tracking-[0.18em] uppercase text-slate-500 mb-2">ROI total</div>
                      <div className="text-3xl font-bold ar-gradient-text">{preview.central.financials.roi_pct.toFixed(0)} %</div>
                    </div>
                    <div className="p-5 text-center">
                      <div className="text-[10px] tracking-[0.18em] uppercase text-slate-500 mb-2">VAN</div>
                      <div className="text-3xl font-bold text-slate-900">{Math.round(preview.central.financials.net_benefit).toLocaleString("fr-FR")} €</div>
                    </div>
                    <div className="p-5 text-center">
                      <div className="text-[10px] tracking-[0.18em] uppercase text-slate-500 mb-2">Payback</div>
                      <div className="text-3xl font-bold text-slate-900">{preview.central.financials.payback_months ?? "—"} mois</div>
                    </div>
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-white/85 to-white/70 backdrop-blur-[2px]">
                    <div className="text-center px-6">
                      <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-slate-900 mb-3">
                        <Lock className="w-4 h-4 text-white" />
                      </div>
                      <div className="text-sm font-semibold text-slate-900">Vos résultats sont prêts</div>
                      <div className="text-xs text-slate-500 mt-1">Saisissez vos coordonnées ci-dessous pour les débloquer.</div>
                    </div>
                  </div>
                </div>
              )}

              <div className="rounded-2xl bg-slate-950 text-white p-6 -mx-2">
                <div className="text-[11px] tracking-[0.18em] uppercase text-slate-400 font-semibold mb-2">Dernière étape</div>
                <div className="font-semibold leading-relaxed">
                  Votre étude est <span className="ar-gradient-text">prête à être calculée</span>. Renseignez vos
                  coordonnées pour la visualiser et recevoir le PDF brandé Ariovis par email.
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <LeadField id="firstname" label="Prénom *" value={lead.firstname} onChange={(v) => updateLead("firstname", v)} required />
                <LeadField id="lastname" label="Nom *" value={lead.lastname} onChange={(v) => updateLead("lastname", v)} required />
              </div>
              <LeadField id="email" label="Email professionnel *" type="email" value={lead.email} onChange={(v) => updateLead("email", v)} required />
              <LeadField id="company" label="Société *" value={lead.company} onChange={(v) => updateLead("company", v)} required />
              <div className="rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 text-xs text-slate-600">
                Secteur d'activité : <b className="text-slate-900">{inputs.sector}</b>
                <span className="text-slate-400"> · </span>
                <button type="button" onClick={() => setStep(0)} className="text-fuchsia-600 hover:underline" data-testid="lead-change-sector">modifier</button>
              </div>
              <p className="text-[11px] text-slate-500 leading-relaxed pt-1">
                En envoyant ce formulaire, vous acceptez d'être contacté par Ariovis. Vos données sont stockées
                dans notre CRM Odoo (hébergé en Belgique, UE) à des fins de prospection commerciale. Aucun cookie
                publicitaire, aucune donnée envoyée à une IA.{" "}
                <a href="/mentions-legales" target="_blank" className="text-fuchsia-600 hover:underline" data-testid="form-legal-link">Mentions légales</a>.
              </p>

              <div className="grid sm:grid-cols-2 gap-3 pt-2">
                <a
                  href={BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="lead-book-meeting"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-white text-slate-900 border border-slate-200 hover:border-slate-300 transition text-sm font-medium"
                >
                  <Calendar className="w-4 h-4" /> Préférez parler à un humain ?
                </a>
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="mt-8 flex items-center justify-between">
          <button
            onClick={onBack}
            disabled={loading}
            data-testid="btn-back"
            className="inline-flex items-center gap-2 px-5 py-3 text-sm text-slate-600 hover:text-slate-900 transition disabled:opacity-50"
          >
            <ArrowLeft className="w-4 h-4" />
            {step === 0 ? "Retour à l'accueil" : "Étape précédente"}
          </button>
          <button
            onClick={onNext}
            disabled={loading || (step === 1 && inputs.nb_users < 250)}
            data-testid="btn-next"
            className="group inline-flex items-center gap-3 px-8 py-3.5 rounded-full bg-slate-900 text-white font-medium tracking-tight transition-all hover:bg-slate-950 ar-glow disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-slate-900"
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 animate-spin" />Calcul + envoi...</>
            ) : step === STEPS.length - 1 ? (
              <><Mail className="w-4 h-4" />Recevoir mon étude<ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" /></>
            ) : (
              <>Étape suivante<ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" /></>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function NumberField({ label, value, onChange, min, max, step, helper, tooltip, testid }) {
  const [display, setDisplay] = useState(value === null || value === undefined ? "" : String(value));
  useEffect(() => {
    setDisplay(value === null || value === undefined ? "" : String(value));
  }, [value]);
  return (
    <div>
      <Label className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
        {label}
        {tooltip && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger type="button" className="inline-flex">
                <HelpCircle className="w-3.5 h-3.5 text-slate-400 hover:text-fuchsia-600 transition" data-testid={`${testid}-tooltip`} />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs text-xs leading-relaxed bg-slate-900 text-white border-slate-800">
                {tooltip}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </Label>
      <Input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={display}
        min={min} max={max} step={step}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === "" || /^\d+$/.test(raw)) {
            setDisplay(raw);
            onChange(raw === "" ? 0 : parseInt(raw, 10));
          }
        }}
        onBlur={() => { if (display === "") setDisplay("0"); }}
        data-testid={testid}
        className="h-12 text-base"
      />
      {helper && <p className="text-xs text-slate-500 mt-2">{helper}</p>}
    </div>
  );
}

function SliderField({ label, value, onChange, min, max, step, format, helper, tooltip, testid }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-4">
        <Label className="text-sm font-medium text-slate-700 flex items-center gap-2">
          {label}
          {tooltip && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger type="button" className="inline-flex">
                  <HelpCircle className="w-3.5 h-3.5 text-slate-400 hover:text-fuchsia-600 transition" data-testid={`${testid}-tooltip`} />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs text-xs leading-relaxed bg-slate-900 text-white border-slate-800">
                  {tooltip}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </Label>
        <span className="text-2xl font-bold ar-gradient-text" data-testid={`${testid}-value`}>{format(value)}</span>
      </div>
      <Slider
        value={[value]}
        min={min} max={max} step={step}
        onValueChange={(v) => onChange(v[0])}
        data-testid={testid}
      />
      {helper && <p className="text-xs text-slate-500 mt-3">{helper}</p>}
    </div>
  );
}

function LeadField({ id, label, value, onChange, type = "text", required }) {
  return (
    <div>
      <Label htmlFor={id} className="text-sm font-medium text-slate-700">{label}</Label>
      <Input
        id={id}
        type={type}
        value={value}
        required={required}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`lead-${id}`}
        className="mt-1.5 h-11"
      />
    </div>
  );
}
